import { useMemo, useState } from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
    AwardIcon,
    CircleAlertIcon,
    RepeatIcon,
    CircleCheckIcon,
    ClockIcon,
    TrendingUpIcon,
    UserMinusIcon,
    UsersIcon,
} from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Empty,
    EmptyDescription,
    EmptyHeader,
    EmptyMedia,
    EmptyTitle,
} from '@/components/ui/empty';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useFetch } from '@/hooks/useFecth';
import { useClub } from './ClubContext';

const PERIOD_OPTIONS = [
    { value: 'mensal', label: 'Mensal' },
    { value: 'trimestral', label: 'Trimestral' },
    { value: 'anual', label: 'Anual' },
];

function formatCurrency(value) {
    return Number(value || 0).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

function MetricCard({ title, value, icon: Icon, subtitle }) {
    return (
        <Card className="h-full gap-0 py-0 transition-colors hover:border-primary/40">
            <CardContent className="flex flex-col gap-1 p-4">
                <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium text-muted-foreground">{title}</p>
                    <Icon className="size-5 shrink-0 text-muted-foreground/60" />
                </div>
                <p className="text-3xl font-semibold tracking-tight tabular-nums">{value}</p>
                {subtitle ? <p className="text-sm text-muted-foreground">{subtitle}</p> : null}
            </CardContent>
        </Card>
    );
}

function DashboardSkeleton() {
    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="flex flex-col gap-2">
                    <Skeleton className="h-8 w-56" />
                    <Skeleton className="h-4 w-72" />
                </div>
                <Skeleton className="h-8 w-64" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {[1, 2, 3].map(i => (
                    <Skeleton key={i} className="h-28 rounded-xl" />
                ))}
            </div>
            <div className="grid gap-4 lg:grid-cols-2">
                <Skeleton className="h-80 rounded-xl" />
                <Skeleton className="h-80 rounded-xl" />
            </div>
        </div>
    );
}

function TopAgreementsCard({ agreements }) {
    return (
        <Card className="h-full">
            <CardHeader>
                <div className="flex items-center justify-between">
                    <CardTitle>Convênios mais resgatados</CardTitle>
                    <AwardIcon className="size-5 text-primary" />
                </div>
            </CardHeader>
            <CardContent>
                {agreements.length ? (
                    <div className="flex flex-col">
                        {agreements.map((agreement, index) => (
                            <div key={agreement.id || agreement.name || index}>
                                <div className="flex items-center gap-3 py-3">
                                    <Avatar className="size-9">
                                        <AvatarFallback className="bg-primary/10 font-semibold text-primary">
                                            {index + 1}
                                        </AvatarFallback>
                                    </Avatar>
                                    <div className="min-w-0 flex-1">
                                        <p className="truncate font-semibold">{agreement.name || 'Convênio'}</p>
                                        {(agreement.category || agreement.subtitle) && (
                                            <p className="truncate text-sm text-muted-foreground">
                                                {agreement.category || agreement.subtitle}
                                            </p>
                                        )}
                                    </div>
                                    <div className="text-right">
                                        <p className="font-semibold">
                                            {(agreement.totalRescues ?? agreement.total ?? 0).toLocaleString('pt-BR')}
                                        </p>
                                        <p className="text-xs text-muted-foreground">resgates</p>
                                    </div>
                                </div>
                                {index !== agreements.length - 1 ? <Separator /> : null}
                            </div>
                        ))}
                    </div>
                ) : (
                    <Empty className="border-0 py-8">
                        <EmptyHeader>
                            <EmptyMedia variant="icon">
                                <AwardIcon />
                            </EmptyMedia>
                            <EmptyTitle>Sem resgates no período</EmptyTitle>
                            <EmptyDescription>
                                Nenhum resgate registrado no período selecionado.
                            </EmptyDescription>
                        </EmptyHeader>
                    </Empty>
                )}
            </CardContent>
        </Card>
    );
}

function ChartCard({ title, dataKey, data, gradientId, stroke, formatter }) {
    return (
        <Card className="h-full">
            <CardHeader>
                <CardTitle>{title}</CardTitle>
            </CardHeader>
            <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={data}>
                        <defs>
                            <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={stroke} stopOpacity={0.3} />
                                <stop offset="95%" stopColor={stroke} stopOpacity={0} />
                            </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                        <Tooltip
                            formatter={value =>
                                formatter ? formatter(value) : Number(value).toLocaleString('pt-BR')
                            }
                            contentStyle={{
                                backgroundColor: '#fff',
                                border: '1px solid #e0e0e0',
                                borderRadius: 8,
                            }}
                        />
                        <Area
                            type="monotone"
                            dataKey={dataKey}
                            stroke={stroke}
                            strokeWidth={2}
                            fill={`url(#${gradientId})`}
                            name="Quantidade"
                        />
                    </AreaChart>
                </ResponsiveContainer>
            </CardContent>
        </Card>
    );
}

export default function ClubeDashboard() {
    const [period, setPeriod] = useState('mensal');
    const { club: clubUpdate, canViewFinance } = useClub();
    const isReseller = Boolean(clubUpdate?.functions?.includes('reseller'));

    const {
        data: overview,
        error: overviewError,
        isFetching: isOverviewFetching,
    } = useFetch(`club/dashboard/overview?period=${period}`);

    const {
        data: evolution,
        error: evolutionError,
        isFetching: isEvolutionFetching,
    } = useFetch(`club/dashboard/evolution?period=${period}`);

    const topAgreements = useMemo(() => overview?.topAgreements || overview?.topConvenios || [], [overview]);
    const subscriberEvolution = useMemo(() => evolution?.assinantes || [], [evolution]);
    const ordersEvolution = useMemo(() => evolution?.orders || evolution?.vendas || [], [evolution]);
    const invoices = overview?.invoices;

    if (overviewError) {
        return (
            <Alert variant="destructive">
                <TrendingUpIcon />
                <AlertTitle>Erro ao carregar dashboard</AlertTitle>
                <AlertDescription>
                    Não foi possível carregar as estatísticas. Tente novamente mais tarde.
                </AlertDescription>
            </Alert>
        );
    }

    if (isOverviewFetching && !overview) {
        return <DashboardSkeleton />;
    }

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div className="flex flex-col gap-1">
                    <h1 className="text-2xl font-semibold tracking-tight">Dashboard do Clube</h1>
                    <p className="text-sm text-muted-foreground">
                        Acompanhe a performance dos seus assinantes, vendas e resgates.
                    </p>
                </div>

                <Tabs value={period} onValueChange={setPeriod}>
                    <TabsList>
                        {PERIOD_OPTIONS.map(option => (
                            <TabsTrigger key={option.value} value={option.value}>
                                {option.label}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                </Tabs>
            </div>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <MetricCard
                    title="Usuários ativos"
                    value={(overview?.users?.active ?? overview?.assinantes?.ativos ?? 0).toLocaleString('pt-BR')}
                    icon={UsersIcon}
                    subtitle={`${(overview?.users?.total ?? overview?.assinantes?.total ?? 0).toLocaleString('pt-BR')} no total`}
                />
                <MetricCard
                    title="Usuários inativos"
                    value={(overview?.users?.inactive ?? overview?.assinantes?.inativos ?? 0).toLocaleString('pt-BR')}
                    icon={UserMinusIcon}
                />
                {clubUpdate?.viewRescue ? (
                    <MetricCard
                        title="Resgates no período"
                        value={(overview?.rescues?.quantidade ?? overview?.rescues?.total ?? 0).toLocaleString('pt-BR')}
                        icon={AwardIcon}
                        subtitle={
                            overview?.rescues?.valorTotal
                                ? formatCurrency(overview.rescues.valorTotal)
                                : undefined
                        }
                    />
                ) : null}
            </div>

            {isReseller && canViewFinance ? (
                <div className="flex flex-col gap-3">
                    <div className="flex flex-col gap-0.5">
                        <h2 className="text-sm font-semibold tracking-tight">Cobranças</h2>
                        <p className="text-xs text-muted-foreground">
                            Pagas no período selecionado · pendentes e atrasadas em aberto · recorrente = assinaturas ativas (anuais divididas por 12)
                        </p>
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <MetricCard
                            title="Pagas"
                            value={formatCurrency(invoices?.paid?.total)}
                            icon={CircleCheckIcon}
                            subtitle={`${(invoices?.paid?.count ?? 0).toLocaleString('pt-BR')} fatura(s)`}
                        />
                        <MetricCard
                            title="Pendentes"
                            value={formatCurrency(invoices?.pending?.total)}
                            icon={ClockIcon}
                            subtitle={`${(invoices?.pending?.count ?? 0).toLocaleString('pt-BR')} fatura(s)`}
                        />
                        <MetricCard
                            title="Atrasadas"
                            value={formatCurrency(invoices?.overdue?.total)}
                            icon={CircleAlertIcon}
                            subtitle={`${(invoices?.overdue?.count ?? 0).toLocaleString('pt-BR')} fatura(s)`}
                        />
                        <MetricCard
                            title="Receita recorrente (mensal)"
                            value={formatCurrency(invoices?.recurring?.mrr)}
                            icon={RepeatIcon}
                            subtitle={`${(invoices?.recurring?.monthly?.count ?? 0).toLocaleString('pt-BR')} mensal(is) · ${(invoices?.recurring?.yearly?.count ?? 0).toLocaleString('pt-BR')} anual(is) ÷ 12${invoices?.recurring?.installment?.count ? ` (${invoices.recurring.installment.count.toLocaleString('pt-BR')} parcelado(s))` : ''}`}
                        />
                    </div>
                </div>
            ) : null}

            {clubUpdate?.viewRescue ? (
                <TopAgreementsCard agreements={topAgreements} />
            ) : null}

            <div className="grid gap-4 lg:grid-cols-2">
                <ChartCard
                    title="Evolução de novos assinantes"
                    dataKey="quantidade"
                    data={subscriberEvolution}
                    gradientId="assinantesGradient"
                    stroke="#635BFF"
                />
                <ChartCard
                    title="Evolução de vendas realizadas"
                    dataKey="quantidade"
                    data={ordersEvolution}
                    gradientId="vendasGradient"
                    stroke="#00D4AA"
                    formatter={value => Number(value).toLocaleString('pt-BR')}
                />
            </div>

            {evolutionError ? (
                <Alert>
                    <TrendingUpIcon />
                    <AlertTitle>Gráficos indisponíveis</AlertTitle>
                    <AlertDescription>
                        Não foi possível carregar os gráficos de evolução. Tente novamente mais tarde.
                    </AlertDescription>
                </Alert>
            ) : null}

            {isEvolutionFetching && evolution ? (
                <p className="text-xs text-muted-foreground">Atualizando gráficos...</p>
            ) : null}
        </div>
    );
}
